/**
 * Caregiver administration page.
 */

import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Briefcase,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Plus,
  Save,
  Search,
  ShieldCheck,
  Trash2,
  UserRound,
  X,
} from 'lucide-react';
import { LoadingSpinner, ErrorMessage } from '@/core/components/index.js';
import { usePermissions } from '@/core/hooks/index.js';
import { useAuth } from '@/core/hooks';
import { useConfirmAction } from '@/app/components';
import { useCaregiverApi, useCaregivers } from '../hooks/index.js';
import type { CaregiverSearchFilters } from '../types/index.js';

type CaregiverFormMode = 'create' | 'edit';

interface AdminCaregiverRecord {
  id?: string;
  organizationId?: string;
  branchIds?: string[];
  primaryBranchId?: string;
  employeeNumber: string;
  firstName: string;
  middleName: string;
  lastName: string;
  preferredName: string;
  dateOfBirth: string;
  email: string;
  phone: string;
  primaryPhone?: {
    number: string;
    type: 'MOBILE' | 'HOME' | 'WORK';
    canReceiveSMS: boolean;
  };
  addressLine1: string;
  addressLine2: string;
  city: string;
  county: string;
  postcode: string;
  role: string;
  roles?: string[];
  employmentType: string;
  employmentStatus: string;
  status: string;
  hireDate: string;
  hourlyRate: number;
  weeklyContractHours: number;
  mileageRate: number;
  complianceStatus: string;
  dbsNumber: string;
  dbsExpiryDate: string;
  rightToWorkStatus: string;
  rightToWorkExpiryDate: string;
  nationalInsuranceNumber: string;
  trainingSummary: string;
  skills: string;
  availability: string;
  emergencyContactName: string;
  emergencyContactRelationship: string;
  emergencyContactPhone: string;
  emergencyContactEmail: string;
  notes: string;
  createdAt?: string;
  updatedAt?: string;
}

const emptyCaregiverForm: AdminCaregiverRecord = {
  organizationId: 'org-1',
  branchIds: ['branch-1'],
  primaryBranchId: 'branch-1',
  employeeNumber: '',
  firstName: '',
  middleName: '',
  lastName: '',
  preferredName: '',
  dateOfBirth: '',
  email: '',
  phone: '',
  primaryPhone: {
    number: '',
    type: 'MOBILE',
    canReceiveSMS: true,
  },
  addressLine1: '',
  addressLine2: '',
  city: '',
  county: '',
  postcode: '',
  role: 'Care Assistant',
  roles: ['CAREGIVER'],
  employmentType: 'FULL_TIME',
  employmentStatus: 'ACTIVE',
  status: 'ACTIVE',
  hireDate: '',
  hourlyRate: 14.5,
  weeklyContractHours: 37.5,
  mileageRate: 0.45,
  complianceStatus: 'COMPLIANT',
  dbsNumber: '',
  dbsExpiryDate: '',
  rightToWorkStatus: 'Verified',
  rightToWorkExpiryDate: '',
  nationalInsuranceNumber: '',
  trainingSummary: '',
  skills: '',
  availability: '',
  emergencyContactName: '',
  emergencyContactRelationship: '',
  emergencyContactPhone: '',
  emergencyContactEmail: '',
  notes: '',
};

function getText(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function getNumber(value: unknown, fallback = 0): number {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
}

function normalizeCaregiver(record: Partial<AdminCaregiverRecord> & Record<string, unknown>): AdminCaregiverRecord {
  const primaryPhone =
    record.primaryPhone && typeof record.primaryPhone === 'object'
      ? (record.primaryPhone as AdminCaregiverRecord['primaryPhone'])
      : undefined;
  const address = record.address && typeof record.address === 'object'
    ? (record.address as Record<string, unknown>)
    : {};
  const emergencyContact = record.emergencyContact && typeof record.emergencyContact === 'object'
    ? (record.emergencyContact as Record<string, unknown>)
    : {};

  return {
    ...emptyCaregiverForm,
    ...record,
    id: getText(record.id),
    organizationId: getText(record.organizationId) || 'org-1',
    branchIds: Array.isArray(record.branchIds) ? record.branchIds.map(String) : ['branch-1'],
    primaryBranchId: getText(record.primaryBranchId) || 'branch-1',
    employeeNumber: getText(record.employeeNumber),
    firstName: getText(record.firstName),
    middleName: getText(record.middleName),
    lastName: getText(record.lastName),
    preferredName: getText(record.preferredName),
    dateOfBirth: getText(record.dateOfBirth).slice(0, 10),
    email: getText(record.email),
    phone: getText(record.phone || primaryPhone?.number),
    primaryPhone: {
      number: getText(primaryPhone?.number || record.phone),
      type: primaryPhone?.type || 'MOBILE',
      canReceiveSMS: primaryPhone?.canReceiveSMS !== false,
    },
    addressLine1: getText(record.addressLine1 || address.line1),
    addressLine2: getText(record.addressLine2 || address.line2),
    city: getText(record.city || address.city),
    county: getText(record.county || address.county || address.state),
    postcode: getText(record.postcode || address.postcode || address.postalCode),
    role: getText(record.role) || 'Care Assistant',
    roles: Array.isArray(record.roles) ? record.roles.map(String) : ['CAREGIVER'],
    employmentType: getText(record.employmentType) || 'FULL_TIME',
    employmentStatus: getText(record.employmentStatus) || getText(record.status) || 'ACTIVE',
    status: getText(record.status) || getText(record.employmentStatus) || 'ACTIVE',
    hireDate: getText(record.hireDate).slice(0, 10),
    hourlyRate: getNumber(record.hourlyRate, 14.5),
    weeklyContractHours: getNumber(record.weeklyContractHours, 37.5),
    mileageRate: getNumber(record.mileageRate, 0.45),
    complianceStatus: getText(record.complianceStatus) || 'COMPLIANT',
    dbsNumber: getText(record.dbsNumber),
    dbsExpiryDate: getText(record.dbsExpiryDate).slice(0, 10),
    rightToWorkStatus: getText(record.rightToWorkStatus) || 'Verified',
    rightToWorkExpiryDate: getText(record.rightToWorkExpiryDate).slice(0, 10),
    nationalInsuranceNumber: getText(record.nationalInsuranceNumber),
    trainingSummary: getText(record.trainingSummary),
    skills: Array.isArray(record.skills) ? record.skills.join(', ') : getText(record.skills),
    availability: getText(record.availability),
    emergencyContactName: getText(record.emergencyContactName || emergencyContact.name),
    emergencyContactRelationship: getText(record.emergencyContactRelationship || emergencyContact.relationship),
    emergencyContactPhone: getText(record.emergencyContactPhone || emergencyContact.phone),
    emergencyContactEmail: getText(record.emergencyContactEmail || emergencyContact.email),
    notes: getText(record.notes),
    createdAt: getText(record.createdAt),
    updatedAt: getText(record.updatedAt),
  };
}

function buildCaregiverPayload(form: AdminCaregiverRecord): Record<string, unknown> {
  const firstName = form.firstName.trim();
  const lastName = form.lastName.trim();
  const phone = form.phone.trim();
  const skills = form.skills
    .split(',')
    .map((skill) => skill.trim())
    .filter(Boolean);

  return {
    ...form,
    firstName,
    middleName: form.middleName.trim(),
    lastName,
    fullName: [firstName, lastName].filter(Boolean).join(' '),
    preferredName: form.preferredName.trim(),
    email: form.email.trim(),
    phone,
    primaryPhone: {
      number: phone,
      type: form.primaryPhone?.type || 'MOBILE',
      canReceiveSMS: form.primaryPhone?.canReceiveSMS !== false,
    },
    address: {
      line1: form.addressLine1.trim(),
      line2: form.addressLine2.trim(),
      city: form.city.trim(),
      county: form.county.trim(),
      postalCode: form.postcode.trim(),
      country: 'UK',
    },
    role: form.role.trim() || 'Care Assistant',
    roles: form.roles?.length ? form.roles : ['CAREGIVER'],
    status: form.status,
    employmentStatus: form.employmentStatus,
    hourlyRate: Number(form.hourlyRate || 0),
    weeklyContractHours: Number(form.weeklyContractHours || 0),
    mileageRate: Number(form.mileageRate || 0),
    skills,
    emergencyContact: {
      name: form.emergencyContactName.trim(),
      relationship: form.emergencyContactRelationship.trim(),
      phone: form.emergencyContactPhone.trim(),
      email: form.emergencyContactEmail.trim(),
    },
  };
}

function caregiverName(carer: Partial<AdminCaregiverRecord>): string {
  return [carer.firstName, carer.lastName].filter(Boolean).join(' ') || carer.email || 'Unnamed carer';
}

function statusClass(status: string): string {
  if (status === 'ACTIVE' || status === 'COMPLIANT') return 'bg-emerald-100 text-emerald-800 border-emerald-200';
  if (status === 'EXPIRING_SOON' || status === 'PENDING_VERIFICATION' || status === 'ON_LEAVE') return 'bg-amber-100 text-amber-800 border-amber-200';
  if (status === 'EXPIRED' || status === 'NON_COMPLIANT' || status === 'SUSPENDED' || status === 'TERMINATED') return 'bg-red-100 text-red-800 border-red-200';
  return 'bg-slate-100 text-slate-700 border-slate-200';
}

const textInputClass =
  'w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-[#6c3a80] focus:ring-2 focus:ring-[#6c3a80]/20';

export const CaregiverList: React.FC = () => {
  const { can, hasAnyRole } = usePermissions();
  const { token } = useAuth();
  const confirmAction = useConfirmAction();
  const caregiverApi = useCaregiverApi();
  const [filters, setFilters] = useState<CaregiverSearchFilters>({});
  const [page] = useState(1);
  const { data, isLoading, error, refetch } = useCaregivers({}, page, 100);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [formMode, setFormMode] = useState<CaregiverFormMode>('create');
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<AdminCaregiverRecord>(emptyCaregiverForm);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const canManageCaregivers = can('caregivers:write') || hasAnyRole(['SUPER_ADMIN', 'ADMIN', 'COORDINATOR', 'MANAGER']);

  const caregivers = useMemo(
    () => (data?.items ?? []).map((item) => normalizeCaregiver(item as unknown as Record<string, unknown>)),
    [data?.items]
  );

  const filteredCaregivers = useMemo(() => {
    const query = (filters.query ?? '').trim().toLowerCase();
    if (!query) return caregivers;
    return caregivers.filter((carer) =>
      [
        carer.firstName,
        carer.lastName,
        carer.email,
        carer.phone,
        carer.employeeNumber,
        carer.role,
        carer.status,
        carer.complianceStatus,
        carer.city,
        carer.postcode,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(query)
    );
  }, [caregivers, filters.query]);

  const selectedCaregiver = useMemo(
    () => filteredCaregivers.find((caregiver) => caregiver.id === selectedId) ?? filteredCaregivers[0] ?? null,
    [filteredCaregivers, selectedId]
  );

  const updateForm = <Key extends keyof AdminCaregiverRecord>(key: Key, value: AdminCaregiverRecord[Key]) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const openCreateForm = () => {
    setFormMode('create');
    setForm({
      ...emptyCaregiverForm,
      employeeNumber: `FH-CG-${String(caregivers.length + 1).padStart(3, '0')}`,
      hireDate: new Date().toISOString().slice(0, 10),
    });
    setFormOpen(true);
  };

  const openEditForm = (caregiver: AdminCaregiverRecord) => {
    setFormMode('edit');
    setForm(normalizeCaregiver(caregiver as unknown as Record<string, unknown>));
    setFormOpen(true);
  };

  const validateForm = () => {
    if (!form.firstName.trim() || !form.lastName.trim()) return 'First name and last name are required.';
    if (!form.email.trim()) return 'Email is required.';
    if (!form.phone.trim()) return 'Phone number is required.';
    if (!form.role.trim()) return 'Role is required.';
    return '';
  };

  const saveCaregiver = async () => {
    const validationError = validateForm();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setIsSaving(true);
    try {
      const payload = buildCaregiverPayload(form);
      if (formMode === 'edit' && form.id) {
        await caregiverApi.updateCaregiver(form.id, payload);
        toast.success('Carer updated');
      } else {
        const created = await caregiverApi.createCaregiver(payload);
        const createdRecord = normalizeCaregiver(created as unknown as Record<string, unknown>);
        setSelectedId(createdRecord.id ?? null);
        toast.success('Carer added');
      }
      setFormOpen(false);
      await refetch();
    } catch (saveError) {
      toast.error(saveError instanceof Error ? saveError.message : 'Could not save carer');
    } finally {
      setIsSaving(false);
    }
  };

  const deleteCaregiver = async (caregiver: AdminCaregiverRecord) => {
    if (!caregiver.id) return;
    const confirmed = await confirmAction({
      title: 'Delete carer record?',
      description: `${caregiverName(caregiver)} will be removed from the caregiver database. This can affect scheduling, payroll and compliance history.`,
      confirmLabel: 'Delete carer',
      tone: 'danger',
    });
    if (!confirmed) return;

    setDeletingId(caregiver.id);
    try {
      await caregiverApi.deleteCaregiver(caregiver.id);
      if (selectedId === caregiver.id) setSelectedId(null);
      toast.success('Carer deleted');
      await refetch();
    } catch (deleteError) {
      toast.error(deleteError instanceof Error ? deleteError.message : 'Could not delete carer');
    } finally {
      setDeletingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" label="Loading carers..." />
      </div>
    );
  }

  if (error) {
    return (
      <ErrorMessage
        message={(error as Error).message || 'Failed to load caregivers'}
        retry={refetch}
      />
    );
  }

  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-[#6c3a80]">Caregiver administration</p>
            <h1 className="mt-1 text-3xl font-bold text-slate-950">Carers</h1>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
              Add, edit, remove and maintain complete carer records used by scheduling, payroll, compliance and the carer portal.
            </p>
          </div>
          <button
            type="button"
            onClick={openCreateForm}
            disabled={!canManageCaregivers}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-[#6c3a80] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#5e3474] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Plus className="h-4 w-4" />
            Add carer
          </button>
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-4">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm font-medium text-slate-500">Total carers</p>
            <p className="mt-2 text-2xl font-bold text-slate-950">{caregivers.length}</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm font-medium text-slate-500">Active</p>
            <p className="mt-2 text-2xl font-bold text-slate-950">{caregivers.filter((item) => item.status === 'ACTIVE').length}</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm font-medium text-slate-500">Compliant</p>
            <p className="mt-2 text-2xl font-bold text-slate-950">{caregivers.filter((item) => item.complianceStatus === 'COMPLIANT').length}</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm font-medium text-slate-500">Write access</p>
            <p className="mt-2 text-lg font-bold text-slate-950">{canManageCaregivers && token ? 'Enabled' : 'Read only'}</p>
          </div>
        </div>
      </section>

      <div className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
        <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center gap-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
            <Search className="h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by name, email, phone, role, city..."
              value={filters.query ?? ''}
              onChange={(event) => setFilters({ ...filters, query: event.target.value })}
              className="min-w-0 flex-1 bg-transparent text-sm outline-none"
            />
          </div>

          <div className="mt-4 max-h-[760px] space-y-3 overflow-y-auto pr-1">
            {filteredCaregivers.map((caregiver) => (
              <button
                key={caregiver.id}
                type="button"
                onClick={() => setSelectedId(caregiver.id ?? null)}
                className={`w-full rounded-lg border p-4 text-left transition ${
                  selectedCaregiver?.id === caregiver.id
                    ? 'border-[#6c3a80] bg-[#6c3a80]/5'
                    : 'border-slate-200 bg-white hover:border-[#6c3a80]/40'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-base font-bold text-slate-950">{caregiverName(caregiver)}</p>
                    <p className="mt-1 truncate text-sm text-slate-500">{caregiver.employeeNumber || 'No employee number'} - {caregiver.role}</p>
                  </div>
                  <span className={`shrink-0 rounded-full border px-2 py-1 text-xs font-semibold ${statusClass(caregiver.status)}`}>
                    {caregiver.status}
                  </span>
                </div>
                <div className="mt-3 grid gap-2 text-sm text-slate-600">
                  <span className="inline-flex items-center gap-2">
                    <Mail className="h-4 w-4 text-slate-400" />
                    {caregiver.email || 'No email'}
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <Phone className="h-4 w-4 text-slate-400" />
                    {caregiver.phone || 'No phone'}
                  </span>
                </div>
              </button>
            ))}
          </div>

          {!filteredCaregivers.length && (
            <div className="py-10 text-center text-sm text-slate-500">
              No carers match the current search.
            </div>
          )}
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          {selectedCaregiver ? (
            <div className="space-y-6">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-wide text-[#6c3a80]">Selected carer</p>
                  <h2 className="mt-1 text-2xl font-bold text-slate-950">{caregiverName(selectedCaregiver)}</h2>
                  <p className="mt-1 text-sm text-slate-500">{selectedCaregiver.employeeNumber || 'No employee number'} - {selectedCaregiver.role}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => openEditForm(selectedCaregiver)}
                    disabled={!canManageCaregivers}
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-[#6c3a80]/30 bg-[#6c3a80]/10 px-3 py-2 text-sm font-semibold text-[#6c3a80] transition hover:bg-[#6c3a80]/15 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Pencil className="h-4 w-4" />
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteCaregiver(selectedCaregiver)}
                    disabled={!canManageCaregivers || deletingId === selectedCaregiver.id}
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Trash2 className="h-4 w-4" />
                    {deletingId === selectedCaregiver.id ? 'Deleting' : 'Delete'}
                  </button>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <DetailGroup icon={<UserRound className="h-5 w-5" />} title="Personal details">
                  <Detail label="Full name" value={caregiverName(selectedCaregiver)} />
                  <Detail label="Preferred name" value={selectedCaregiver.preferredName || '-'} />
                  <Detail label="Date of birth" value={selectedCaregiver.dateOfBirth || '-'} />
                  <Detail label="National Insurance" value={selectedCaregiver.nationalInsuranceNumber || '-'} />
                </DetailGroup>

                <DetailGroup icon={<Phone className="h-5 w-5" />} title="Contact">
                  <Detail label="Email" value={selectedCaregiver.email || '-'} />
                  <Detail label="Phone" value={selectedCaregiver.phone || '-'} />
                  <Detail label="SMS" value={selectedCaregiver.primaryPhone?.canReceiveSMS ? 'Allowed' : 'Not allowed'} />
                  <Detail label="Emergency contact" value={selectedCaregiver.emergencyContactName || '-'} />
                </DetailGroup>

                <DetailGroup icon={<MapPin className="h-5 w-5" />} title="Address">
                  <Detail label="Line 1" value={selectedCaregiver.addressLine1 || '-'} />
                  <Detail label="Line 2" value={selectedCaregiver.addressLine2 || '-'} />
                  <Detail label="City" value={selectedCaregiver.city || '-'} />
                  <Detail label="Postcode" value={selectedCaregiver.postcode || '-'} />
                </DetailGroup>

                <DetailGroup icon={<Briefcase className="h-5 w-5" />} title="Employment and payroll">
                  <Detail label="Employment type" value={selectedCaregiver.employmentType} />
                  <Detail label="Hire date" value={selectedCaregiver.hireDate || '-'} />
                  <Detail label="Hourly rate" value={`£${Number(selectedCaregiver.hourlyRate || 0).toFixed(2)}`} />
                  <Detail label="Weekly hours" value={`${selectedCaregiver.weeklyContractHours || 0}h`} />
                </DetailGroup>

                <DetailGroup icon={<ShieldCheck className="h-5 w-5" />} title="Compliance">
                  <Detail label="Compliance status" value={selectedCaregiver.complianceStatus} />
                  <Detail label="DBS number" value={selectedCaregiver.dbsNumber || '-'} />
                  <Detail label="DBS expiry" value={selectedCaregiver.dbsExpiryDate || '-'} />
                  <Detail label="Right to work" value={selectedCaregiver.rightToWorkStatus || '-'} />
                </DetailGroup>

                <DetailGroup icon={<ShieldCheck className="h-5 w-5" />} title="Skills and availability">
                  <Detail label="Skills" value={selectedCaregiver.skills || '-'} />
                  <Detail label="Availability" value={selectedCaregiver.availability || '-'} />
                  <Detail label="Training" value={selectedCaregiver.trainingSummary || '-'} />
                  <Detail label="Notes" value={selectedCaregiver.notes || '-'} />
                </DetailGroup>
              </div>
            </div>
          ) : (
            <div className="py-16 text-center">
              <UserRound className="mx-auto h-12 w-12 text-slate-300" />
              <h2 className="mt-4 text-xl font-bold text-slate-950">No carer selected</h2>
              <p className="mt-2 text-sm text-slate-500">Add a carer or select one from the list.</p>
            </div>
          )}
        </section>
      </div>

      {formOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/40 p-4">
          <div className="mx-auto max-w-6xl rounded-lg bg-white shadow-xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white p-5">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-[#6c3a80]">
                  {formMode === 'edit' ? 'Edit carer' : 'Add carer'}
                </p>
                <h2 className="mt-1 text-2xl font-bold text-slate-950">
                  {formMode === 'edit' ? caregiverName(form) : 'New carer record'}
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setFormOpen(false)}
                className="rounded-md border border-slate-200 p-2 text-slate-500 transition hover:bg-slate-50"
                aria-label="Close form"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="grid gap-6 p-5 lg:grid-cols-2">
              <FormSection title="Personal details">
                <FormInput label="Employee number" value={form.employeeNumber} onChange={(value) => updateForm('employeeNumber', value)} />
                <FormInput label="First name" value={form.firstName} onChange={(value) => updateForm('firstName', value)} required />
                <FormInput label="Middle name" value={form.middleName} onChange={(value) => updateForm('middleName', value)} />
                <FormInput label="Last name" value={form.lastName} onChange={(value) => updateForm('lastName', value)} required />
                <FormInput label="Preferred name" value={form.preferredName} onChange={(value) => updateForm('preferredName', value)} />
                <FormInput label="Date of birth" type="date" value={form.dateOfBirth} onChange={(value) => updateForm('dateOfBirth', value)} />
                <FormInput label="National Insurance number" value={form.nationalInsuranceNumber} onChange={(value) => updateForm('nationalInsuranceNumber', value)} />
              </FormSection>

              <FormSection title="Contact and address">
                <FormInput label="Email" type="email" value={form.email} onChange={(value) => updateForm('email', value)} required />
                <FormInput label="Phone" value={form.phone} onChange={(value) => updateForm('phone', value)} required />
                <label className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700">
                  <input
                    type="checkbox"
                    checked={form.primaryPhone?.canReceiveSMS !== false}
                    onChange={(event) =>
                      updateForm('primaryPhone', {
                        number: form.phone,
                        type: form.primaryPhone?.type || 'MOBILE',
                        canReceiveSMS: event.target.checked,
                      })
                    }
                  />
                  Can receive SMS
                </label>
                <FormInput label="Address line 1" value={form.addressLine1} onChange={(value) => updateForm('addressLine1', value)} />
                <FormInput label="Address line 2" value={form.addressLine2} onChange={(value) => updateForm('addressLine2', value)} />
                <FormInput label="City" value={form.city} onChange={(value) => updateForm('city', value)} />
                <FormInput label="County" value={form.county} onChange={(value) => updateForm('county', value)} />
                <FormInput label="Postcode" value={form.postcode} onChange={(value) => updateForm('postcode', value)} />
              </FormSection>

              <FormSection title="Employment and payroll">
                <FormInput label="Role" value={form.role} onChange={(value) => updateForm('role', value)} required />
                <FormSelect
                  label="Employment type"
                  value={form.employmentType}
                  options={['FULL_TIME', 'PART_TIME', 'PER_DIEM', 'CONTRACT', 'TEMPORARY', 'SEASONAL']}
                  onChange={(value) => updateForm('employmentType', value)}
                />
                <FormSelect
                  label="Employment status"
                  value={form.employmentStatus}
                  options={['ACTIVE', 'ON_LEAVE', 'SUSPENDED', 'TERMINATED', 'RETIRED']}
                  onChange={(value) => {
                    updateForm('employmentStatus', value);
                    updateForm('status', value === 'ACTIVE' ? 'ACTIVE' : value);
                  }}
                />
                <FormInput label="Hire date" type="date" value={form.hireDate} onChange={(value) => updateForm('hireDate', value)} />
                <FormInput label="Hourly rate" type="number" value={String(form.hourlyRate)} onChange={(value) => updateForm('hourlyRate', Number(value))} />
                <FormInput label="Weekly contract hours" type="number" value={String(form.weeklyContractHours)} onChange={(value) => updateForm('weeklyContractHours', Number(value))} />
                <FormInput label="Mileage rate" type="number" value={String(form.mileageRate)} onChange={(value) => updateForm('mileageRate', Number(value))} />
              </FormSection>

              <FormSection title="Compliance and training">
                <FormSelect
                  label="Compliance status"
                  value={form.complianceStatus}
                  options={['COMPLIANT', 'PENDING_VERIFICATION', 'EXPIRING_SOON', 'EXPIRED', 'NON_COMPLIANT']}
                  onChange={(value) => updateForm('complianceStatus', value)}
                />
                <FormInput label="DBS number" value={form.dbsNumber} onChange={(value) => updateForm('dbsNumber', value)} />
                <FormInput label="DBS expiry date" type="date" value={form.dbsExpiryDate} onChange={(value) => updateForm('dbsExpiryDate', value)} />
                <FormInput label="Right to work status" value={form.rightToWorkStatus} onChange={(value) => updateForm('rightToWorkStatus', value)} />
                <FormInput label="Right to work expiry" type="date" value={form.rightToWorkExpiryDate} onChange={(value) => updateForm('rightToWorkExpiryDate', value)} />
                <FormTextarea label="Training summary" value={form.trainingSummary} onChange={(value) => updateForm('trainingSummary', value)} />
                <FormTextarea label="Skills, separated by comma" value={form.skills} onChange={(value) => updateForm('skills', value)} />
              </FormSection>

              <FormSection title="Emergency contact">
                <FormInput label="Contact name" value={form.emergencyContactName} onChange={(value) => updateForm('emergencyContactName', value)} />
                <FormInput label="Relationship" value={form.emergencyContactRelationship} onChange={(value) => updateForm('emergencyContactRelationship', value)} />
                <FormInput label="Phone" value={form.emergencyContactPhone} onChange={(value) => updateForm('emergencyContactPhone', value)} />
                <FormInput label="Email" type="email" value={form.emergencyContactEmail} onChange={(value) => updateForm('emergencyContactEmail', value)} />
              </FormSection>

              <FormSection title="Availability and notes">
                <FormTextarea label="Availability" value={form.availability} onChange={(value) => updateForm('availability', value)} />
                <FormTextarea label="Administrator notes" value={form.notes} onChange={(value) => updateForm('notes', value)} />
              </FormSection>
            </div>

            <div className="sticky bottom-0 flex flex-col gap-3 border-t border-slate-200 bg-white p-5 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setFormOpen(false)}
                className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                <X className="h-4 w-4" />
                Cancel
              </button>
              <button
                type="button"
                onClick={saveCaregiver}
                disabled={isSaving || !canManageCaregivers}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-[#6c3a80] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#5e3474] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                {isSaving ? 'Saving' : 'Save carer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function DetailGroup({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div className="mb-3 flex items-center gap-2 text-[#6c3a80]">
        {icon}
        <h3 className="font-bold text-slate-950">{title}</h3>
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid gap-1 text-sm sm:grid-cols-[150px_1fr]">
      <span className="font-medium text-slate-500">{label}</span>
      <span className="min-w-0 break-words text-slate-900">{value}</span>
    </div>
  );
}

function FormSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <h3 className="text-base font-bold text-slate-950">{title}</h3>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">{children}</div>
    </section>
  );
}

function FormInput({
  label,
  value,
  onChange,
  type = 'text',
  required = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  required?: boolean;
}) {
  return (
    <label className="space-y-1 text-sm font-medium text-slate-700">
      <span>{label}{required ? ' *' : ''}</span>
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={textInputClass}
      />
    </label>
  );
}

function FormSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}) {
  return (
    <label className="space-y-1 text-sm font-medium text-slate-700">
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)} className={textInputClass}>
        {options.map((option) => (
          <option key={option} value={option}>
            {option.replaceAll('_', ' ')}
          </option>
        ))}
      </select>
    </label>
  );
}

function FormTextarea({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="space-y-1 text-sm font-medium text-slate-700 sm:col-span-2">
      <span>{label}</span>
      <textarea
        value={value}
        onChange={(event) => onChange(event.target.value)}
        rows={3}
        className={textInputClass}
      />
    </label>
  );
}
